import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserPlus2, LogIn, UserPlus } from "lucide-react";
import { PageHero } from "../components/shared/Primitives";
import { useHandoverSession } from "../hooks/useHandoverSession";

const PROFILE_PATH = "/workspace/pro-carer-profile";
const CARER_ENTITLEMENT = "carer_free";

/**
 * Entry point for the site-wide purple "Post My Profile" navigation
 * action (Step 3, 2026-08-24). A thin router in front of the EXISTING
 * native Carer Profile editor (/workspace/pro-carer-profile) — it does
 * not render a form and does not touch that route's own permissions
 * (still enforced there by HandoverGate, now requiring only carer_free).
 *
 * Branches on the visitor's handover session, mirroring
 * CareNeedsEntry.jsx's exact pattern for "Post My Care Needs":
 *   - Already signed in as a registered Carer -> straight through to
 *     the profile editor (which get-or-creates, so there is never a
 *     duplicate profile).
 *   - Signed in without a carer entitlement (family-only) -> explains
 *     why the action isn't part of their account.
 *   - Logged out -> offered BOTH signing in (existing member) and
 *     joining Carer Starter free — either way landing back on the
 *     profile editor once complete, via the existing ?next= handover
 *     redirect mechanism (MagicLinkRequest / JoinFree persist it,
 *     MagicLinkChecking honours it after redeem).
 */
export default function PostMyProfileEntry() {
  const { ready, active, entitlements } = useHandoverSession();
  const carerEntitled = ready && active && entitlements.includes(CARER_ENTITLEMENT);

  useEffect(() => {
    if (carerEntitled) {
      window.location.replace(PROFILE_PATH);
    }
  }, [carerEntitled]);

  if (!ready || carerEntitled) {
    return (
      <div className="container-jcc py-24 text-center text-ink-muted" data-testid="post-profile-entry-loading">
        Loading…
      </div>
    );
  }

  if (active) {
    // Signed in via the handover session, but no carer entitlement
    // (family-only) — per spec, family-only accounts should not be
    // routed into carer profile creation.
    return (
      <div data-testid="post-profile-entry-not-carer">
        <PageHero
          eyebrow="Post My Profile"
          title="This action is for registered JCC Carers."
          description="Creating a Carer Profile is part of joining JCC as a Carer. Your current account doesn't include carer access yet."
          accent="purple"
          bgTint="carer"
        >
          <Link to={`/join/carer-starter?next=${encodeURIComponent(PROFILE_PATH)}`} className="btn-purple" data-testid="post-profile-entry-join-carer">
            Join as a Carer — free <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/" className="btn-outline" data-testid="post-profile-entry-back-home">
            Back to homepage
          </Link>
        </PageHero>
      </div>
    );
  }

  return (
    <div data-testid="post-profile-entry-logged-out">
      <PageHero
        eyebrow="Post My Profile"
        title="Create your free Carer Profile."
        description="Publishing a Carer Profile is free for every registered JCC Carer. Sign in if you're already a member, or join free — either way you'll land straight back here to build your profile."
        accent="purple"
        bgTint="carer"
      >
        <Link
          to={`/auth/handover/request?next=${encodeURIComponent(PROFILE_PATH)}`}
          className="btn-outline"
          data-testid="post-profile-entry-signin"
        >
          <LogIn className="w-4 h-4" /> Sign in
        </Link>
        <Link
          to={`/join/carer-starter?next=${encodeURIComponent(PROFILE_PATH)}`}
          className="btn-purple"
          data-testid="post-profile-entry-join"
        >
          <UserPlus className="w-4 h-4" /> Join free & create my profile
        </Link>
      </PageHero>

      <section className="section pt-0">
        <div className="container-jcc max-w-3xl">
          <div className="card-base p-6 flex items-start gap-3">
            <UserPlus2 className="w-6 h-6 text-purple shrink-0 mt-0.5" strokeWidth={1.75} />
            <p className="text-sm text-ink-muted leading-relaxed">
              No card required — creating and publishing your Carer Profile is free. Once you sign in or join,
              you&rsquo;ll be taken straight to your profile editor.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
